import { useState } from "react";
import ApproveUserDialog from "./ApproveUserDialog";
import UserInfoFields from "./UserInfoFields";
import { Alert, Box, Button, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Section from "components/Section";
import OutlinedCard from "components/OutlinedCard";
import { useDeleteUserMutation } from "app/api/users";
import ConfirmModal from "features/confirmModal/ConfirmModal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "context/UserContext";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import UpsertUserDialog from "dialogs/UpsertUserDialog";

const UserData: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthContext();
  const userToApprove = useUserContext();
  const userIsVerified = userToApprove?.validatedAt !== null;
  const currentUserIsNotApproved = userToApprove?.state === "PENDING";
  const [deleteUser] = useDeleteUserMutation();

  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);

  const currentUserCanApprove =
    currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.USER_ADMIN;

  const handleDelete = async () => {
    try {
      await deleteUser({ userId: userToApprove.id });
      toast.success("Användaren raderades");
      setShowDeleteDialog(false);
      navigate("/users");
    } catch (error) {
      toast.error("Det gick inte att radera användaren");
    }
  };

  return (
    <Section>
      <OutlinedCard>
        {currentUserCanApprove && !currentUserIsNotApproved && (
          <Box sx={{ float: "right" }}>
            <Tooltip arrow title="Redigera användare">
              <IconButton onClick={() => setShowUpdateDialog(true)}>
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip arrow title="Radera användare">
              <IconButton onClick={() => setShowDeleteDialog(true)} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <UserInfoFields user={userToApprove} />

        {currentUserCanApprove && userIsVerified && currentUserIsNotApproved && (
          <Alert
            severity="info"
            sx={{ mt: 1 }}
            action={
              <Button color="inherit" onClick={() => setShowApproveDialog(true)}>
                Godkänn
              </Button>
            }
          >
            Användaren väntar på godkännande
            {userToApprove.requestedCompanyName && (
              <>
                {" "}
                för företaget <strong>{userToApprove.requestedCompanyName}</strong>
              </>
            )}
          </Alert>
        )}
      </OutlinedCard>

      {showApproveDialog && (
        <ApproveUserDialog user={userToApprove} onClose={() => setShowApproveDialog(false)} />
      )}

      {showDeleteDialog && (
        <ConfirmModal
          isLoading={false}
          handleClose={() => setShowDeleteDialog(false)}
          handleConfirm={handleDelete}
          show={true}
          title="Ta bort"
          message="Är du säker på att du vill ta bort den här användaren?"
          confirmLabel="Ja, ta bort"
          dismissLabel="Avbryt"
        />
      )}

      {showUpdateDialog && (
        <UpsertUserDialog user={userToApprove} onClose={() => setShowUpdateDialog(false)} />
      )}
    </Section>
  );
};

export default UserData;
